<script setup>
import { computed, onMounted, ref } from "vue";
import { useExpediaStore } from "@stores/expedia";
import { useActivityStore } from "@stores/activity";
import IconPin from "@components/icons/Pin.vue";
import IconArrowUpRight from "@components/icons/ArrowUpRight.vue";
import FilterSelect from "@components/activities/FilterSelect.vue";
import FiltersSheet from "@components/activities/FiltersSheet.vue";

const expediaStore = useExpediaStore();
const activityStore = useActivityStore();

const filtersOpen = ref(false);
const listEl = ref(null);

// Filter controls are driven by the store; the city list cascades from the
// selected country and is disabled until a country is chosen.
const controls = computed(() => [
    {
        key: "country",
        label: "Filter by country",
        options: activityStore.countryOptions,
        value: activityStore.selections.country,
    },
    {
        key: "city",
        label: "Filter by city",
        options: activityStore.cityOptions,
        value: activityStore.selections.city,
        disabled: !activityStore.selections.country,
    },
    {
        key: "category",
        label: "Filter by category",
        options: activityStore.categoryOptions,
        value: activityStore.selections.category,
    },
    {
        key: "order",
        label: "Order by",
        options: activityStore.orderOptions,
        value: activityStore.selections.order,
    },
]);

function location(item) {
    const city = item.city?.name;
    const country = item.city?.country?.name;
    return [city, country].filter(Boolean).join(", ");
}

// "10.0" -> "10", "9.6" -> "9.6"
function formatRating(rating) {
    return Number(rating).toString();
}

function onScroll() {
    const el = listEl.value;
    if (!el || activityStore.loading || !activityStore.hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        activityStore.loadMore();
    }
}

onMounted(() => activityStore.setup());
</script>

<template>
    <Transition name="panel" appear>
        <div
            class="activities-panel border-marine-500 bg-marine-800 flex flex-col gap-6 p-6 lg:border-r lg:border-b"
        >
            <!-- Heading -->
            <div class="flex flex-col gap-10">
                <div class="flex items-center justify-between gap-4">
                    <p
                        class="h6 leading-[0.9] font-bold tracking-tight uppercase"
                    >
                        Shop Activities
                    </p>

                    <!-- Mobile filters entry point -->
                    <button
                        type="button"
                        class="flex shrink-0 items-center gap-2 lg:hidden"
                        @click="filtersOpen = true"
                    >
                        <span
                            class="body-md leading-none font-extrabold tracking-wide text-yellow-400 uppercase"
                        >
                            Filters ({{ activityStore.activeCount }})
                        </span>
                        <svg
                            class="size-4 text-yellow-400"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M2 4h12M4 8h8M6 12h4"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <!-- Filters -->
                <div
                    class="flex flex-wrap items-end gap-2 max-lg:hidden lg:flex-nowrap"
                >
                    <FilterSelect
                        v-for="control in controls"
                        :key="control.key"
                        :label="control.label"
                        :options="control.options"
                        :model-value="control.value"
                        :disabled="control.disabled"
                        class="min-w-[160px] flex-1"
                        @update:model-value="
                            activityStore.setFilter(control.key, $event)
                        "
                    />

                    <button
                        type="button"
                        class="bg-marine-600 relative flex h-12 shrink-0 items-center justify-center overflow-hidden px-6 max-lg:w-full"
                        @click="activityStore.reset()"
                    >
                        <span
                            class="leading-none font-extrabold tracking-wide text-yellow-400 uppercase"
                        >
                            Reset
                        </span>
                        <span
                            class="absolute right-0 bottom-0 size-3 bg-yellow-400"
                            style="
                                clip-path: polygon(100% 0, 100% 100%, 0 100%);
                            "
                        />
                    </button>
                </div>
            </div>

            <!-- List -->
            <div
                ref="listEl"
                class="activities-list min-h-0 flex-1 overflow-y-auto"
                @scroll.passive="onScroll"
            >
                <ul
                    v-if="activityStore.activities.length"
                    class="grid grid-cols-1 gap-2 lg:grid-cols-2"
                >
                    <li
                        v-for="(item, i) in activityStore.activities"
                        :key="item.id"
                        class="activity-card"
                        :style="{ '--delay': (i % 24) * 0.03 + 's' }"
                    >
                        <a
                            :href="expediaStore.href(item.guide_url)"
                            target="_blank"
                            rel="noopener"
                            class="bg-marine-600 hover:bg-marine-500 flex h-24 items-center gap-4 p-4 transition-colors duration-150"
                        >
                            <div class="flex min-w-0 flex-1 flex-col gap-3">
                                <p
                                    class="body-md truncate leading-[0.9] font-bold uppercase"
                                >
                                    {{ item.name }}
                                </p>
                                <div class="flex items-center gap-3">
                                    <div class="flex min-w-0 items-center gap-1">
                                        <IconPin
                                            class="size-3 shrink-0 text-white/50"
                                        />
                                        <p
                                            class="body-xs faded-text truncate leading-[0.9] font-medium uppercase"
                                        >
                                            {{ location(item) }}
                                        </p>
                                    </div>
                                    <span
                                        v-if="item.rating"
                                        class="body-xs flex shrink-0 items-center gap-1 leading-[0.9] font-bold text-yellow-400 uppercase"
                                    >
                                        <i class="fa-solid fa-star text-[0.85em]" />
                                        {{ formatRating(item.rating) }}
                                    </span>
                                </div>
                            </div>
                            <div
                                class="text-marine-800 flex size-6 shrink-0 items-center justify-center rounded-sm bg-yellow-400"
                            >
                                <IconArrowUpRight class="size-3.5" />
                            </div>
                        </a>
                    </li>
                </ul>

                <!-- Empty state (only after a fetch has actually returned) -->
                <div
                    v-else-if="activityStore.hasFetched && !activityStore.loading"
                    class="flex h-full items-center justify-center p-6"
                >
                    <p
                        class="body-md faded-text text-center leading-[0.9] font-bold uppercase"
                    >
                        No activities match your filters
                    </p>
                </div>

                <!-- Loading indicator (initial load + pagination) -->
                <div
                    v-if="activityStore.loading || !activityStore.hasFetched"
                    class="flex items-center justify-center p-6"
                    :class="activityStore.activities.length ? '' : 'h-full'"
                >
                    <i
                        class="fa-solid fa-circle-notch fa-spin text-2xl text-yellow-400"
                    />
                </div>
            </div>
        </div>
    </Transition>

    <FiltersSheet
        :open="filtersOpen"
        :controls="controls"
        @update="activityStore.setFilter($event.key, $event.value)"
        @reset="activityStore.reset()"
        @close="filtersOpen = false"
    />
</template>

<style scoped>
.activities-panel {
    grid-column: 3 / -3;
    grid-row: 3 / -2;
    z-index: 3;
    min-height: 0;
}

.activity-card {
    animation: activity-card-in 0.3s ease-out both;
    animation-delay: var(--delay, 0s);
}

@keyframes activity-card-in {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.activities-list {
    scrollbar-width: thin;
    scrollbar-color: var(--color-yellow-400) transparent;
}

.activities-list::-webkit-scrollbar {
    width: 5px;
}

.activities-list::-webkit-scrollbar-thumb {
    background: var(--color-yellow-400);
    border-radius: 9999px;
}

.activities-list::-webkit-scrollbar-track {
    background: transparent;
}

@media (max-width: 1024px) {
    .activities-panel {
        grid-column: 1 / -1;
        grid-row: 2 / -1;
    }
}
</style>
